/**
 * UnifiedBentoVisuals - 統一外框風格 + 原始 Bento 內容
 * 外框：統一的 grid 背景和容器樣式
 * 內容：完全照抄 bento-master 原始視覺
 */
import React from 'react';
import { Icon } from '@iconify/react';

// ========== 統一容器包裝組件 ==========
const UnifiedContainer = ({ children }) => {
  return (
    <div className="relative w-full h-72 bg-[#fdfdfd] rounded-2xl border border-gray-100 overflow-hidden shadow-sm mb-4">
      {/* Grid Background */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />
      {/* 內容區域 */}
      <div className="relative w-full h-full flex items-center justify-center">
        {children}
      </div>
    </div>
  );
};

// ========== Q2: 產品資訊 - ProductCard ==========
export const ContactCardVisual = () => {
  return (
    <UnifiedContainer>
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Floating Features - 商品特色介紹 */}
        <div className="absolute top-6 left-6 bg-blue-50 p-3 px-4 rounded-2xl shadow-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Icon icon="mdi:sparkles" className="w-5 h-5 text-blue-600" />
            <div className="text-[13px] font-bold text-blue-900">AI 智能生成</div>
          </div>
          <div className="text-[10px] text-blue-600">自動創作高品質內容</div>
        </div>

        <div className="absolute top-4 right-8 bg-blue-50 p-3 px-4 rounded-2xl shadow-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Icon icon="mdi:clock-outline" className="w-5 h-5 text-blue-600" />
            <div className="text-[13px] font-bold text-blue-900">排程發布</div>
          </div>
          <div className="text-[10px] text-blue-600">多平台定時自動發文</div>
        </div>

        <div className="absolute bottom-8 left-8 bg-blue-50 p-3 px-4 rounded-2xl shadow-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Icon icon="mdi:message-reply-text" className="w-5 h-5 text-blue-600" />
            <div className="text-[13px] font-bold text-blue-900">智能回覆</div>
          </div>
          <div className="text-[10px] text-blue-600">AI 自動回覆留言互動</div>
        </div>

        <div className="absolute bottom-5 right-10 bg-blue-50 p-3 px-4 rounded-2xl shadow-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <Icon icon="mdi:chart-line" className="w-5 h-5 text-blue-600" />
            <div className="text-[13px] font-bold text-blue-900">數據分析</div>
          </div>
          <div className="text-[10px] text-blue-600">即時洞察成效追蹤</div>
        </div>

        {/* Main Product Card */}
        <div className="relative w-[65%] bg-white rounded-2xl shadow-2xl p-5 z-10 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
              <Icon icon="mdi:robot-outline" className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="text-[15px] font-bold text-slate-900">AI 自動化助手</div>
              <div className="text-[11px] text-slate-500">
                智能社群管理 · <span className="text-blue-600 font-semibold">全自動化</span>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3 flex flex-col gap-2.5">
            <div className="flex items-center gap-2 text-[11px] text-slate-700">
              <Icon icon="mdi:check-circle" className="w-4 h-4 text-blue-600" />
              智能內容生成與排程
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-700">
              <Icon icon="mdi:check-circle" className="w-4 h-4 text-blue-600" />
              多平台自動發布與回覆
            </div>
          </div>
        </div>
      </div>
    </UnifiedContainer>
  );
};

// ========== Q3: 目標族群 - FamilyAvatars (原始內容) ==========
export const FamilyAvatarsVisual = () => {
  const avatars = [
    { bg: 'bg-green-100', img: '/avatars/avatar1.jpg' },
    { bg: 'bg-blue-100', img: '/avatars/avatar2.jpg' },
    { bg: 'bg-pink-100', img: '/avatars/avatar3.jpg' },
    { bg: 'bg-purple-100', img: '/avatars/avatar4.jpg' },
    { bg: 'bg-orange-100', img: '/avatars/avatar5.jpg' },
  ];

  return (
    <UnifiedContainer>
      <div className="relative w-full h-full flex items-center justify-center">
        <div className="relative w-64 h-64">
          {/* Top Avatar */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden">
            <img src={avatars[0].img} className="w-full h-full object-cover" alt="" />
          </div>
          {/* Left Avatar */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden">
            <img src={avatars[1].img} className="w-full h-full object-cover" alt="" />
          </div>
          {/* Right Avatar */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden">
            <img src={avatars[3].img} className="w-full h-full object-cover" alt="" />
          </div>
          {/* Bottom Avatar */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden">
            <img src={avatars[4].img} className="w-full h-full object-cover" alt="" />
          </div>
          {/* Center Main Avatar */}
          <div className="absolute inset-0 m-auto w-32 h-32 rounded-full border-[6px] border-white shadow-xl overflow-hidden z-10">
            <img src={avatars[2].img} className="w-full h-full object-cover" alt="" />
          </div>
        </div>
        {/* Heart Icon */}
        <div className="absolute bottom-12 left-6 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg border-2 border-white">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
          </svg>
        </div>
      </div>
    </UnifiedContainer>
  );
};

// ========== Q4: 產品特色 - ManualData (原始內容) ==========
export const ManualDataVisual = () => {
  const chips = [
    { label: 'Download', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', rotate: '-rotate-12' },
    { label: 'Edit', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5', rotate: 'rotate-6' },
    { label: 'Copy', icon: 'M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2', rotate: 'rotate-3' },
  ];

  return (
    <UnifiedContainer>
      <div className="relative w-full h-full flex items-center justify-center p-8">
        <div className="relative z-10 flex flex-wrap justify-center gap-2 max-w-[280px]">
          {chips.map((chip, idx) => (
            <div
              key={idx}
              className={`bg-slate-900 text-white text-[11px] font-medium px-4 py-2 rounded-full flex items-center gap-2 shadow-md transition-transform hover:scale-105 ${chip.rotate}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={chip.icon} />
              </svg>
              {chip.label}
            </div>
          ))}
        </div>
      </div>
    </UnifiedContainer>
  );
};

// ========== Q5: 行銷連結 - AIGenerate (原始內容) ==========
export const AIGenerateVisual = () => {
  return (
    <UnifiedContainer>
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        {/* Generate Button - 完全照抄原始 */}
        <button className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-400 via-indigo-400 to-cyan-400 text-white font-bold shadow-2xl flex items-center gap-3 hover:shadow-3xl transition-shadow">
          Generate Anything
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </button>
      </div>
    </UnifiedContainer>
  );
};

// ========== Q6: 留言回覆 - InstagramComments (原始內容) ==========
export const InstagramCommentsVisual = () => {
  const comments = [
    { user: 'c.yu.an_', text: 'This bento layout is fire! 🔥', time: '2h', likes: '12', avatar: '/avatars/anna_avatar.jpg' },
    { user: 'zet.lu', text: 'Love the smooth animations here.', time: '4h', likes: '8', avatar: '/avatars/neil_avatar.jpg', liked: true },
    { user: 'linzuweii', text: 'Gemini 3 Flash integration is seamless. Great work on the visual fidelity.', time: '1d', likes: '45', avatar: '/avatars/elvis_avatar.jpg' },
  ];

  return (
    <div className="relative w-full h-80 bg-[#fdfdfd] rounded-2xl border border-gray-100 overflow-hidden shadow-sm mb-4">
      {/* Grid Background */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      {/* Instagram Comments - 完全照抄原始 */}
      <div className="relative w-full h-full rounded-xl border-b border-gray-100 flex flex-col overflow-hidden bg-white/90 backdrop-blur-sm">
        {/* Header */}
        <div className="p-3 border-b border-gray-50 flex items-center justify-between">
          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 p-[2px]">
            <div className="w-full h-full bg-white rounded-full p-[1px]">
              <img src="/avatars/avatar1.jpg" className="w-full h-full rounded-full object-cover" alt="" />
            </div>
          </div>
          <div className="text-[13px] font-bold text-slate-900">Comments</div>
          <div className="w-8" /> {/* Spacer */}
        </div>

        {/* Comment List */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
          {comments.map((comment, i) => (
            <div key={i} className="flex gap-3">
              <img src={comment.avatar} className="w-8 h-8 rounded-full border border-gray-100" alt="" />
              <div className="flex-1">
                <div className="text-[12px] leading-tight text-slate-900">
                  <span className="font-bold mr-1.5">{comment.user}</span>
                  {comment.text}
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-[11px] text-slate-400 font-medium">
                  <span>{comment.time}</span>
                  <span>{comment.likes} likes</span>
                  <span className="cursor-pointer hover:text-slate-600">Reply</span>
                </div>
              </div>
              <div className="pt-1">
                <svg
                  className={`w-3 h-3 ${comment.liked ? 'text-red-500 fill-current' : 'text-slate-300'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Input Simulation */}
        <div className="p-3 border-t border-gray-50 flex items-center gap-3">
          <div className="w-7 h-7 rounded-full border border-gray-100 overflow-hidden">
            <img src="/avatars/avatar1.jpg" className="w-full h-full object-cover" alt="" />
          </div>
          <div className="flex-1 bg-gray-50 rounded-full px-4 py-1.5 text-[12px] text-slate-400 flex items-center justify-between">
            <span>Add a comment...</span>
            <span className="text-blue-500 font-bold">Post</span>
          </div>
          <span className="text-sm">❤️</span>
        </div>
      </div>
    </div>
  );
};
